import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Container, Grid, Typography } from '@mui/material';
// import { useDispatch } from 'react-redux';
// import { CreateContactRequest } from '../../redux/actions/contactRequestsActions';
import './styles.scss';

// ─────────────────────────────────────────────────────────────────────────────
// Static data
// ─────────────────────────────────────────────────────────────────────────────

const steps = [
  {
    n: '01',
    title: 'Ingest',
    body: 'Draft CBP Form 7501 before filing, a filed 7501, or your own entry pivot table. Header and all line items, including continuation sheets, extracted into structured rows.',
  },
  {
    n: '02',
    title: 'Derive',
    body: 'A deterministic engine computes, from ingested statutory rules, the Chapter 99 code chain, rates, duty basis, and line roles that the entry facts require.',
  },
  {
    n: '03',
    title: 'Compare',
    body: 'Declared against derived, line by line. Every discrepancy is surfaced as a finding rather than silently reconciled.',
  },
  {
    n: '04',
    title: 'Explain',
    body: 'Each finding carries a citation back to the governing statutory text, in the words CBP published, for the reviewer and the auditor.',
  },
];

const layers = [
  {
    layer: 'Obligation engine',
    fn: 'Determines what is owed. A deterministic query over human-approved statutory rules.',
  },
  {
    layer: 'Explanation engine',
    fn: "Explains the obligation engine's output to importers, brokers, and auditors.",
  },
  {
    layer: 'Change detection',
    fn: 'Surfaces statutory changes and proposes corresponding rule updates.',
  },
];

const commitments = [
  {
    term: 'Human approval',
    detail: 'No machine-extracted statutory rule takes effect until a person reviews and approves it.',
  },
  {
    term: 'Verbatim source text',
    detail: 'Statutory text is stored in the words CBP published. Auditors need the original language, so the original language is what we keep.',
  },
  {
    term: 'No hard deletes',
    detail: 'Rules are closed with effective-date ranges, never erased. The treatment that applied on the date of your entry stays retrievable.',
  },
  {
    term: 'Citation on every finding',
    detail: 'Each discrepancy resolves to the governing statutory text. Nothing is surfaced that cannot be traced.',
  },
];

const derivesToday = [
  {
    title: 'Section 232 metal content',
    detail: 'Steel, aluminum, and copper provisions, including melt-and-pour and smelt-and-cast declaration rules and the associated Chapter 99 heading families.',
  },
  {
    title: 'Chapter 98 documentation validation',
    detail: 'The 9802.00.40, .50, .60, and .80 subheadings — American goods returned, repairs and alterations — and their per-subheading documentation requirements.',
  },
  {
    title: 'Civil aircraft exemption',
    detail: 'General Note 6 eligibility, used to validate whether a claimed civil-aircraft exemption legitimately fires.',
  },
  { title: 'Entry Type 01', detail: 'US formal entries.' },
  {
    title: 'Native-text CBP Form 7501 and entry pivot tables',
    detail: 'Header and all line items, including continuation sheets.',
  },
];

const buildPath = [
  'Section 301 provisions and exclusion mechanisms',
  'IEEPA refund handling; Section 122',
  'Free-trade-agreement rules of origin, including USMCA regional value content and tariff-shift rules',
  'Softwood lumber and SSRN provisions',
  'Entry types other than 01',
  'Scanned, non-native-text 7501 PDFs',
  'Commercial invoice and bill-of-lading extraction',
];

const audienceFacts = [
  {
    term: 'Primary buyer',
    detail: 'The importer of record — the trade compliance lead, or the CFO or controller at companies without a dedicated compliance function.',
  },
  {
    term: 'Best fit',
    detail: 'High formal-entry volume with complex classification exposure: significant Chapter 98 activity, or heavy Section 232 metal content, or both.',
  },
  {
    term: 'Secondary users',
    detail: 'Customs brokers and outside auditors, who consume the explanation layer rather than the derivation.',
  },
  {
    term: 'Not a fit today',
    detail: 'Low formal-entry volume, entry types other than 01, or exposure concentrated entirely in build-path areas.',
  },
];

const glossary = [
  { term: 'Entry Type 01', detail: 'A formal US import entry.' },
  { term: 'CBP Form 7501', detail: 'The Entry Summary — the filing that declares codes, values, rates, and duties.' },
  {
    term: 'Chapter 99',
    detail: 'The HTSUS chapter carrying tariff-action codes — Section 232, Section 301, IEEPA, Section 201, MTB, quotas. These stack on top of the base classification.',
  },
  {
    term: 'Chapter 98',
    detail: 'Special classification provisions, including American goods returned and repairs or alterations.',
  },
  { term: 'HTSUS', detail: 'Harmonized Tariff Schedule of the United States, published by the USITC.' },
  { term: 'PSC', detail: 'Post Summary Correction — a broker amendment to an entry that has already been filed.' },
  { term: 'CF-29', detail: "CBP's Notice of Action. The letter that tells you an entry was wrong." },
  { term: 'SPI', detail: 'Special Program Indicator — a code claiming a trade-program benefit.' },
  { term: 'MPF / HMF', detail: 'Merchandise Processing Fee and Harbor Maintenance Fee.' },
  {
    term: 'Melt and pour / smelt and cast',
    detail: 'Origin declarations required for steel, aluminum, and copper content under Section 232.',
  },
  { term: 'General Note 6', detail: 'The HTSUS provision governing civil aircraft eligibility.' },
  {
    term: 'Companion line',
    detail: 'A Chapter 99 line that must be declared alongside another line for the entry to be complete.',
  },
];

// const emptyForm = {
//   company: '',
//   email: '',
//   role: '',
//   entryVolume: '',
//   chapters: '',
//   cf29: '',
//   note: '',
// };

const FactList = ({ facts }) => (
  <Box component="dl" className="trd-facts">
    {facts.map(({ term, detail }) => (
      <Box key={term} className="trd-facts__row">
        <Typography component="dt" className="trd-facts__term">{term}</Typography>
        <Typography component="dd" className="trd-facts__detail">{detail}</Typography>
      </Box>
    ))}
  </Box>
);

const Claim = ({ headline, why, className = '' }) => (
  <Box className={`trd-claim ${className}`.trim()}>
    <Typography className="trd-claim__headline">{headline}</Typography>
    <Typography className="trd-claim__why">{why}</Typography>
  </Box>
);

const TradePage = () => {
  const navigate = useNavigate();
  // const dispatch = useDispatch();

  // const [formData, setFormData] = useState(emptyForm);
  // const [submitted, setSubmitted] = useState(false);
  // const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // const handleInputChange = (e) => {
  //   const { name, value } = e.target;
  //   setFormData((prevState) => ({ ...prevState, [name]: value }));
  // };

  // The contact-requests endpoint takes { name, email, message }; the design
  // partner qualifiers are folded into the message body so they reach the same inbox.
  // const buildMessage = () => [
  //   'LUSID Trade — design partner enquiry',
  //   `Role: ${formData.role || '—'}`,
  //   `Annual formal entry volume: ${formData.entryVolume}`,
  //   `Primary exposure (chapters / trade actions): ${formData.chapters}`,
  //   `CF-29 in the last 24 months: ${formData.cf29 || '—'}`,
  //   `Notes: ${formData.note || '—'}`,
  // ].join('\n');

  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   setLoading(true);

  //   try {
  //     await dispatch(
  //       CreateContactRequest(
  //         { name: formData.company, email: formData.email, message: buildMessage() },
  //         setLoading
  //       )
  //     );
  //     setFormData(emptyForm);
  //     setSubmitted(true);
  //     setTimeout(() => setSubmitted(false), 6000);
  //   } catch (error) {
  //     console.error('Trade design partner submission failed:', error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  return (
    <Box className="trd-page">

      {/* ── Status strip ─────────────────────────────────────── */}
      <Box className="trd-status">
        <Container maxWidth="lg">
          <Typography className="trd-status__text">
            <strong>Status:</strong> in development. Working with a design partner ahead of general
            availability. Not yet generally released.
          </Typography>
        </Container>
      </Box>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <Box className="trd-hero">
        <Box className="trd-hero__grid-bg" />
        <Container maxWidth="lg">
          <Box className="trd-hero__content">
            <Typography className="trd-hero__overline">LUSID Trade</Typography>
            <Typography variant="h1" className="trd-hero__title">
              Know what your entry should say before it is filed.
            </Typography>
            <Typography className="trd-hero__subtitle">
              LUSID Trade independently derives the Chapter 99 code chain, rates, duty basis, and
              entry line roles that your entry facts require under the HTSUS — then compares that
              derivation, line by line, against what your broker declared. Discrepancies surface
              while you can still act on them.
            </Typography>
            <Box className="trd-hero__cta-row">
              <Button
                variant="contained"
                className="trd-hero__cta"
                onClick={() => navigate('/contact-us')}
              >
                Become a design partner
              </Button>
              <Typography className="trd-hero__note">Entry Type 01, US formal entries.</Typography>
            </Box>

            <Claim
              className="trd-claim--invert"
              headline="The AI never decides what you owe."
              why="Obligation is determined by a deterministic engine running human-approved statutory rules. The language model explains that determination and helps propose rule updates for human review. It does not compute your duty, and it cannot override the engine that does."
            />
          </Box>
        </Container>
      </Box>

      {/* ── The problem ──────────────────────────────────────── */}
      <Box className="trd-section">
        <Container maxWidth="lg">
          <Box className="trd-section__head">
            <Typography className="trd-section__overline">The problem</Typography>
            <Typography variant="h2" className="trd-section__title">
              By the time CBP tells you the entry was wrong, your only remedy is a correction
            </Typography>
            <Typography className="trd-section__lede">
              Importers rely on customs brokers to assign Chapter 99 secondary classifications —
              Section 232, Section 301, IEEPA, Section 201, MTB, quota provisions. Brokers get them
              wrong. The error is invisible until a CF-29 arrives, often months later, and by then
              the entry is filed. The remedy is a Post Summary Correction, after the fact, with
              penalty and interest exposure attached.
            </Typography>
            <Typography className="trd-section__lede">
              The rule set does not hold still. The HTSUS is republished as Basic Editions plus
              numbered Revisions; the rule sets LUSID Trade ingests span Revision 32 of 2025 through
              the 2026 Basic Edition and its subsequent Revisions — more than ten rule-set changes
              across a partial two-year window. A classification that was correct at the time of one
              entry is not necessarily correct at the next.
            </Typography>
            <Typography className="trd-section__lede">
              Section 232 metal-content provisions are a concentrated source of this. Steel,
              aluminum, and copper content require melt-and-pour and smelt-and-cast country
              declarations that are easy to omit — and omitted declarations are precisely what CF-29s
              cite. That single error class is what this engine was built to catch.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ── How it works ─────────────────────────────────────── */}
      <Box className="trd-section trd-section--tinted" id="trd-how">
        <Container maxWidth="lg">
          <Box className="trd-section__head">
            <Typography className="trd-section__overline">How it works</Typography>
            <Typography variant="h2" className="trd-section__title">
              An independent second derivation, not a spell-check
            </Typography>
            <Typography className="trd-section__lede">
              LUSID Trade does not review your broker's work for internal consistency. It derives the
              answer independently from the entry facts and the governing statutory text, then puts
              the two side by side.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {steps.map(({ n, title, body }) => (
              <Grid item xs={12} sm={6} md={3} key={n}>
                <Box className="trd-step">
                  <Typography className="trd-step__number">{n}</Typography>
                  <Typography className="trd-step__title">{title}</Typography>
                  <Typography className="trd-step__desc">{body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Claim
            className="trd-claim--spaced"
            headline="The extractor transcribes what the document says — including the errors."
            why="It never corrects a code to what it thinks is right. Your broker's errors are the output of this product, not noise to be cleaned up before you see them."
          />
        </Container>
      </Box>

      {/* ── Why you can trust the output ─────────────────────── */}
      <Box className="trd-section" id="trd-trust">
        <Container maxWidth="lg">
          <Box className="trd-section__head">
            <Typography className="trd-section__overline">Why you can trust the output</Typography>
            <Typography variant="h2" className="trd-section__title">
              Three layers, and only one of them decides anything
            </Typography>
            <Typography className="trd-section__lede">
              The reasonable first question about any AI product in this category is whether it is a
              language model guessing at tariff treatment. It is not, and the architecture is the
              reason.
            </Typography>
          </Box>

          <Box className="trd-table-wrap">
            <Box component="table" className="trd-table">
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" className="trd-table__col-layer">Layer</Box>
                  <Box component="th">Function</Box>
                </Box>
              </Box>
              <Box component="tbody">
                {layers.map(({ layer, fn }) => (
                  <Box component="tr" key={layer}>
                    <Box component="td" className="trd-table__layer" data-label="Layer">{layer}</Box>
                    <Box component="td" data-label="Function">{fn}</Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <Typography variant="h2" className="trd-section__title trd-section__title--sub">
            Four commitments that hold regardless of what the model says
          </Typography>
          <FactList facts={commitments} />
        </Container>
      </Box>

      {/* ── Coverage ─────────────────────────────────────────── */}
      <Box className="trd-section trd-section--tinted" id="trd-coverage">
        <Container maxWidth="lg">
          <Box className="trd-section__head">
            <Typography className="trd-section__overline">Coverage</Typography>
            <Typography variant="h2" className="trd-section__title">
              Stated precisely, because overstating it would defeat the point
            </Typography>
            <Typography className="trd-section__lede">
              A compliance product that exaggerates its scope is worse than no product. Below is what
              the engine derives today and what is on the build path. Build-path items carry no dates
              — they ship when they meet the accuracy bar, not before.
            </Typography>
          </Box>

          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Typography className="trd-cov__heading">Derives today</Typography>
              <Box component="ul" className="trd-cov trd-cov--now">
                {derivesToday.map(({ title, detail }) => (
                  <Box component="li" key={title} className="trd-cov__item">
                    {title}
                    <span className="trd-cov__detail">{detail}</span>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className="trd-cov__heading">On the build path — no committed dates</Typography>
              <Box component="ul" className="trd-cov trd-cov--next">
                {buildPath.map((item) => (
                  <Box component="li" key={item} className="trd-cov__item">{item}</Box>
                ))}
              </Box>
            </Grid>
          </Grid>

          <Typography className="trd-section__footnote">
            Design partners help set the order of the build path. If an item above is your primary
            exposure, that is worth telling us.
          </Typography>
        </Container>
      </Box>

      {/* ── Who it is for ────────────────────────────────────── */}
      <Box className="trd-section">
        <Container maxWidth="lg">
          <Box className="trd-section__head">
            <Typography className="trd-section__overline">Who it is for</Typography>
            <Typography variant="h2" className="trd-section__title">
              Importers of record with real classification exposure
            </Typography>
          </Box>
          <FactList facts={audienceFacts} />
        </Container>
      </Box>

      {/* <Box className="trd-partner" id="trd-partner">
        <Container maxWidth="lg">
          <Box className="trd-partner__head">
            <Typography className="trd-partner__overline">Design partners</Typography>
            <Typography variant="h2" className="trd-partner__title">
              We are taking a small number of design partners before general availability
            </Typography>
            <Typography className="trd-partner__lede">
              A design partner runs real entries through the engine, tells us where the derivation
              disagrees with reality, and helps set the order of the build path. In a product where
              accuracy is the entire proposition, that feedback is worth more than early revenue.
            </Typography>
          </Box>

          {submitted && (
            <Alert severity="success" className="trd-partner__alert">
              Thank you — your enquiry is in. We will be in touch at the email you provided.
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} className="trd-form">
            <Box className="trd-form__field">
              <label htmlFor="t-co">Company<span className="trd-form__req">required</span></label>
              <input id="t-co" name="company" value={formData.company} onChange={handleInputChange} required />
            </Box>
            <Box className="trd-form__field">
              <label htmlFor="t-em">Work email<span className="trd-form__req">required</span></label>
              <input id="t-em" name="email" type="email" value={formData.email} onChange={handleInputChange} required />
            </Box>
            <Box className="trd-form__field">
              <label htmlFor="t-role">Your role</label>
              <input
                id="t-role"
                name="role"
                placeholder="e.g. Trade compliance manager"
                value={formData.role}
                onChange={handleInputChange}
              />
            </Box>
            <Box className="trd-form__field">
              <label htmlFor="t-vol">Annual formal entry volume<span className="trd-form__req">required</span></label>
              <select id="t-vol" name="entryVolume" value={formData.entryVolume} onChange={handleInputChange} required>
                <option value="">Select</option>
                {entryVolumes.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </Box>
            <Box className="trd-form__field trd-form__field--full">
              <label htmlFor="t-ch">
                Which HTSUS chapters or trade actions carry most of your exposure?
                <span className="trd-form__req">required</span>
              </label>
              <input
                id="t-ch"
                name="chapters"
                placeholder="e.g. Chapter 98 repairs, Section 232 steel content, Section 301"
                value={formData.chapters}
                onChange={handleInputChange}
                required
              />
            </Box>
            <Box className="trd-form__field trd-form__field--full">
              <label htmlFor="t-cf">Have you received a CF-29 in the last 24 months?</label>
              <select id="t-cf" name="cf29" value={formData.cf29} onChange={handleInputChange}>
                <option value="">Select</option>
                {cf29Options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Box>
            <Box className="trd-form__field trd-form__field--full">
              <label htmlFor="t-note">Anything else we should know</label>
              <textarea id="t-note" name="note" rows={3} value={formData.note} onChange={handleInputChange} />
            </Box>
            <Box className="trd-form__field trd-form__field--full">
              <Button type="submit" variant="contained" disabled={loading} className="trd-form__submit">
                {loading ? <CircularProgress size={18} thickness={5} sx={{ color: '#fff' }} /> : 'Become a design partner'}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box> */}

      {/* ── Glossary ─────────────────────────────────────────── */}
      <Box className="trd-section trd-section--tinted" id="trd-glossary">
        <Container maxWidth="lg">
          <Box className="trd-section__head">
            <Typography className="trd-section__overline">Reference</Typography>
            <Typography variant="h2" className="trd-section__title">Terms used on this page</Typography>
          </Box>
          <Box component="dl" className="trd-gloss">
            {glossary.map(({ term, detail }) => (
              <Box key={term} className="trd-gloss__row">
                <Typography component="dt" className="trd-gloss__term">{term}</Typography>
                <Typography component="dd" className="trd-gloss__detail">{detail}</Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── Disclosure ───────────────────────────────────────── */}
      <Box className="trd-disclosure">
        <Container maxWidth="lg">
          <Typography className="trd-disclosure__text">
            LUSID Trade produces classification and duty analysis for internal review. It is not
            legal advice, it is not a customs ruling, and it does not substitute for the filing
            responsibility of a licensed customs broker or for the importer of record's own
            obligation to exercise reasonable care under 19 U.S.C. § 1484.
          </Typography>
        </Container>
      </Box>

    </Box>
  );
};

export default TradePage;
